import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type ResultadoBaja, type Rubro } from "@/lib/api";
import { mensajeBaja, validarNombreRubro } from "@/lib/proveedores";

type Dialogo = { tipo: "form"; rubro: Rubro | null } | { tipo: "borrar"; rubro: Rubro } | null;

/** Lógica de Configuración → Rubros (sin JSX). Sólo admin: lista todos, archivados incluidos. */
export function useRubrosTab() {
  const { fid } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/rubros`;
  const lista = useApiResource<Rubro[]>(`${base}?archivados=true`);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [nombre, setNombre] = useState("");
  const [mostrarError, setMostrarError] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const accionGuardar = useAsyncAction((id: number | null, n: string) =>
    id === null ? api.post<Rubro>(base, { nombre: n }) : api.patch<Rubro>(`${base}/${id}`, { nombre: n }),
  );
  const accionEstado = useAsyncAction((id: number, activo: boolean) => api.patch<Rubro>(`${base}/${id}`, { activo }));
  const accionBorrar = useAsyncAction((id: number) => api.delete<ResultadoBaja>(`${base}/${id}`));

  const errorNombre = validarNombreRubro(nombre);
  const cerrar = () => setDialogo(null);

  const abrirForm = (rubro: Rubro | null) => {
    setNombre(rubro?.nombre ?? "");
    setMostrarError(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", rubro });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarError(true);
    if (dialogo?.tipo !== "form" || errorNombre) return;
    if (await accionGuardar.run(dialogo.rubro?.id ?? null, nombre.trim())) {
      cerrar();
      lista.recargar();
    }
  };

  const cambiarEstado = async (rubro: Rubro) => {
    setAviso(null);
    if (await accionEstado.run(rubro.id, !rubro.activo)) lista.recargar();
  };

  const abrirBorrar = (rubro: Rubro) => {
    accionBorrar.limpiarError();
    setDialogo({ tipo: "borrar", rubro });
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    const r = await accionBorrar.run(dialogo.rubro.id);
    if (!r) return;
    setAviso(mensajeBaja(r.resultado, dialogo.rubro.nombre));
    cerrar();
    lista.recargar();
  };

  return {
    rubros: lista.data ?? [],
    cargando: lista.loading && !lista.data,
    errorCarga: lista.data ? null : (lista.error?.message ?? null),
    reintentar: lista.recargar,
    aviso,
    dialogo,
    cerrar,
    nombre,
    setNombre,
    errorNombre: mostrarError ? errorNombre : null,
    abrirNuevo: () => abrirForm(null),
    abrirRenombrar: (r: Rubro) => abrirForm(r),
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    cambiarEstado,
    errorEstado: accionEstado.error,
    abrirBorrar,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
